import { create } from "zustand";
import { DateTime } from "luxon";

export interface TimetableZoomState {
  zoomLevel: number;
  centerTime: string; // Current time as center point
  scrollPosition: number; // Horizontal scroll position in pixels
  viewportWidth: number; // Current viewport width
  minZoom: number;
  maxZoom: number;
  defaultZoom: number;
  actualTimelineHours: number; // The actual number of hours in the timeline

  // Actions
  zoomIn: (centerPoint?: number) => void;
  zoomOut: (centerPoint?: number) => void;
  resetZoom: () => void;
  setZoomLevel: (level: number, centerPoint?: number) => void;
  setViewportWidth: (width: number) => void;
  setScrollPosition: (position: number) => void;
  setActualTimelineHours: (hours: number) => void;
  getVisibleHoursRange: () => {
    startHour: number;
    endHour: number;
    totalHours: number;
  };
}

const MIN_ZOOM = 0.6; // Can zoom out to show up to 48 hours (48 = 24 / 0.5)
const MAX_ZOOM = 5.0; // Can zoom in for fine details
const ZOOM_STEP = 1.2; // 20% zoom steps

const useTimetableZoomStore = create<TimetableZoomState>((set, get) => ({
  zoomLevel: 1.0, // 1.0 = 24 hours visible, 2.0 = 12 hours, 0.5 = 48 hours
  centerTime: DateTime.now().toFormat("HH:mm"),
  scrollPosition: 0,
  viewportWidth: 1200, // Default viewport width
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  defaultZoom: 1.0,
  actualTimelineHours: 24, // Default to 24 hours

  getVisibleHoursRange: () => {
    const { zoomLevel } = get();
    // Base: 24 hours at zoom level 1.0
    const baseHours = 24;
    const visibleHours = baseHours / zoomLevel;

    // For now, always start from hour 0, but this could be made dynamic
    return {
      startHour: 0,
      endHour: Math.ceil(visibleHours),
      totalHours: Math.ceil(visibleHours),
    };
  },

  setActualTimelineHours: (hours: number) => {
    const state = get();
    // Calculate the minimum zoom level that would show all actual hours
    // Base: 24 hours at zoom level 1.0, so minZoom = 24 / actualHours
    const calculatedMinZoom = 24 / hours;
    const newMinZoom = Math.max(calculatedMinZoom, MIN_ZOOM); // Don't go below MIN_ZOOM (0.5)

    set({
      actualTimelineHours: hours,
      minZoom: newMinZoom,
      // If current zoom is below new minimum, adjust it
      zoomLevel: Math.max(state.zoomLevel, newMinZoom),
    });
  },

  zoomIn: (centerPoint?: number) => {
    const { zoomLevel, maxZoom, scrollPosition } = get();
    const newZoom = Math.min(zoomLevel * ZOOM_STEP, maxZoom);

    // Calculate new scroll position to maintain center point
    let newScrollPosition = scrollPosition;
    if (centerPoint !== undefined) {
      const zoomFactor = newZoom / zoomLevel;
      const centerOffset = centerPoint - scrollPosition;
      newScrollPosition = centerPoint - centerOffset * zoomFactor;
    }

    set({
      zoomLevel: newZoom,
      scrollPosition: Math.max(0, newScrollPosition),
    });
  },

  zoomOut: (centerPoint?: number) => {
    const { zoomLevel, minZoom, scrollPosition } = get();
    const newZoom = Math.max(zoomLevel / ZOOM_STEP, minZoom);

    // Calculate new scroll position to maintain center point
    let newScrollPosition = scrollPosition;
    if (centerPoint !== undefined) {
      const zoomFactor = newZoom / zoomLevel;
      const centerOffset = centerPoint - scrollPosition;
      newScrollPosition = centerPoint - centerOffset * zoomFactor;
    }

    set({
      zoomLevel: newZoom,
      scrollPosition: Math.max(0, newScrollPosition),
    });
  },

  resetZoom: () => {
    set({
      zoomLevel: 1.0, // Reset to 24 hours visible
      scrollPosition: 0,
    });
  },

  setZoomLevel: (level: number, centerPoint?: number) => {
    const { minZoom, maxZoom, scrollPosition, zoomLevel } = get();
    const clampedLevel = Math.max(minZoom, Math.min(level, maxZoom));

    // Calculate new scroll position to maintain center point
    let newScrollPosition = scrollPosition;
    if (centerPoint !== undefined) {
      const zoomFactor = clampedLevel / zoomLevel;
      const centerOffset = centerPoint - scrollPosition;
      newScrollPosition = centerPoint - centerOffset * zoomFactor;
    }

    set({
      zoomLevel: clampedLevel,
      scrollPosition: Math.max(0, newScrollPosition),
    });
  },

  setViewportWidth: (width: number) => {
    set({ viewportWidth: width });
  },

  setScrollPosition: (position: number) => {
    set({ scrollPosition: Math.max(0, position) });
  },
}));

export default useTimetableZoomStore;
